import { Controller } from "@hotwired/stimulus"

// Controls sticky video behavior on mobile
// Switches between normal flow and fixed positioning based on scroll
// Reads top offset from CSS custom property --sticky-video-top
export default class extends Controller {
  connect() {
    this.videoElement = this.element
    this.placeholder = null
    this.isFixed = false

    // Bind handlers
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleOrientationChange = this.handleOrientationChange.bind(this)

    // Initialize if on mobile portrait
    this.initializeIfMobile()

    // Always listen for resize/orientation to handle transitions
    window.addEventListener("resize", this.handleResize, { passive: true })
    window.addEventListener("orientationchange", this.handleOrientationChange)
  }

  disconnect() {
    clearTimeout(this.resizeTimeout)
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleResize)
    window.removeEventListener("orientationchange", this.handleOrientationChange)
    this.cleanup()
  }

  initializeIfMobile() {
    if (!this.isMobilePortrait()) {
      // Ensure we're in normal state if not mobile portrait
      this.cleanup()
      window.removeEventListener("scroll", this.handleScroll)
      return
    }

    // Get top offset from CSS custom property
    this.topValue = this.getTopValueFromCSS()

    // Get the initial position of the video (must be in normal state)
    this.initialTop = this.videoElement.getBoundingClientRect().top + window.scrollY

    // Listen for scroll
    window.addEventListener("scroll", this.handleScroll, { passive: true })

    // Initial check
    this.handleScroll()
  }

  getTopValueFromCSS() {
    // Read --sticky-video-top from the container element
    const container = this.element.closest("[data-context]")
    if (container) {
      const value = getComputedStyle(container).getPropertyValue("--sticky-video-top")
      if (value) {
        return parseInt(value, 10) || 40
      }
    }
    return 40 // Default fallback
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.reinitialize()
    }, 100)
  }

  handleOrientationChange() {
    // Wait for orientation change to complete
    setTimeout(() => {
      this.reinitialize()
    }, 100)
  }

  reinitialize() {
    // Clean up current state
    this.cleanup()
    window.removeEventListener("scroll", this.handleScroll)
    this.isFixed = false

    // Re-initialize based on current viewport
    this.initializeIfMobile()
  }

  cleanup() {
    // Reset video element styles
    this.videoElement.style.position = ""
    this.videoElement.style.top = ""
    this.videoElement.style.left = ""
    this.videoElement.style.right = ""
    this.videoElement.style.width = ""
    this.videoElement.style.zIndex = ""
    this.videoElement.style.marginInline = ""

    // Remove placeholder
    this.removePlaceholder()
  }

  isMobilePortrait() {
    // 800px corresponds to $breakpoint-m (50em) defined in stylesheets/viewports.scss
    return window.innerWidth < 800 && window.matchMedia("(orientation: portrait)").matches
  }

  handleScroll() {
    if (!this.isMobilePortrait()) {
      this.makeNormal()
      return
    }

    const scrollY = window.scrollY
    const triggerPoint = this.initialTop - this.topValue

    if (scrollY >= triggerPoint && !this.isFixed) {
      this.makeFixed()
    } else if (scrollY < triggerPoint && this.isFixed) {
      this.makeNormal()
    }
  }

  makeFixed() {
    if (this.isFixed) return

    // Create placeholder to maintain layout
    this.createPlaceholder()

    // Apply fixed positioning
    this.videoElement.style.position = "fixed"
    this.videoElement.style.top = `${this.topValue}px`
    this.videoElement.style.left = "0"
    this.videoElement.style.right = "0"
    this.videoElement.style.width = "100vw"
    this.videoElement.style.zIndex = "1003"
    // Reset margins that CSS sets for full-bleed (they cause offset when fixed)
    this.videoElement.style.marginInline = "0"

    this.isFixed = true
  }

  makeNormal() {
    if (!this.isFixed) return

    this.cleanup()
    this.isFixed = false
  }

  createPlaceholder() {
    if (this.placeholder) return

    const rect = this.videoElement.getBoundingClientRect()
    this.placeholder = document.createElement("div")
    this.placeholder.style.height = `${rect.height}px`
    this.placeholder.style.width = "100%"
    this.videoElement.parentNode.insertBefore(this.placeholder, this.videoElement.nextSibling)
  }

  removePlaceholder() {
    if (this.placeholder) {
      this.placeholder.remove()
      this.placeholder = null
    }
  }
}
